import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faUserGraduate,
  faChalkboardTeacher,
  faSchool,
  faBook,
  faMarker,
  faChartLine,
  faUsers,
  faChalkboard,
  faLayerGroup,
  faBookOpen,
  faSignOutAlt,
  faFileAlt,
  faDatabase,
  faFileDownload,
  faFileInvoiceDollar,
  faChevronLeft,
  faChevronRight,
  faUser,
  faBell,
  faCog,
  faTimes,
  faHistory,
  faSyncAlt,
  faEnvelope,
  faMobileAlt,
  faLock,
  faKey,
  faCheckCircle,
  faClock,
  faQrcode,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import "./sidebar.css";

// API endpoints
// const AUTH_BASE_URL = "https://mhsdev.online/MHS-WEB/api/users.php";
const AUTH_BASE_URL = "https://mhsdev.online/MHS-WEB/api/users.php";

const CACHE_KEY = "dashboard_cache";

const Sidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState(3);
  const [activeItem, setActiveItem] = useState("/");

  // Logout modal state
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileStep, setProfileStep] = useState(1); // 1: edit, 2: verify OTP
  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    mobile: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [otpCode, setOtpCode] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [codeTimer, setCodeTimer] = useState(0);

  // Load user data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const authToken = localStorage.getItem("authToken") || "";

  // Update active item based on current route
  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location]);

  // Close sidebar on mobile when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        window.innerWidth <= 768 &&
        !event.target.closest(".sidebar") &&
        !event.target.closest(".sidebar-toggle")
      ) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Timer for OTP expiry
  useEffect(() => {
    let timer;
    if (codeTimer > 0) {
      timer = setTimeout(() => setCodeTimer(codeTimer - 1), 1000);
    } else if (codeTimer === 0 && profileStep === 2) {
      Swal.fire({
        title: "OTP Expired",
        text: "The verification code has expired. Please request a new one.",
        icon: "warning",
        confirmButtonText: "OK",
      });
    }
    return () => clearTimeout(timer);
  }, [codeTimer, profileStep]);

  // Toggle sidebar
  const toggleSidebar = () => setIsCollapsed(!isCollapsed);
  const toggleMobileSidebar = () => setIsMobileOpen(!isMobileOpen);

  // ---------- LOGOUT (unchanged) ----------
  const handleLogout = async () => {
    setIsLoggingOut(true);

    try {
      const token = localStorage.getItem("authToken") || "";
      const response = await fetch(`${AUTH_BASE_URL}?action=logout`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();
      if (data.success) {
        localStorage.clear();
        sessionStorage.clear();
        localStorage.removeItem(CACHE_KEY);
        setTimeout(() => window.location.href = "/", 1500);
      } else {
        localStorage.clear();
        sessionStorage.clear();
        setTimeout(() => window.location.href = "/", 1000);
      }
    } catch (error) {
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => window.location.href = "/", 1000);
    }
  };

  // ---------- PROFILE MODAL HANDLERS ----------
  const openProfileModal = () => {
    // Load current user data from localStorage
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    setProfileData({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "", // stored from API
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setProfileStep(1);
    setOtpCode("");
    setCodeTimer(0);
    setShowProfileModal(true);
  };

  const handleProfileInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOtpInputChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
  };

  // Step 1: Send OTP before saving changes
  const handleSaveProfile = async (e) => {
    e.preventDefault();

    // Validate inputs
    if (!profileData.name.trim()) {
      Swal.fire("Validation", "Name is required", "warning");
      return;
    }
    if (!profileData.email.trim() || !/\S+@\S+\.\S+/.test(profileData.email)) {
      Swal.fire("Validation", "Valid email is required", "warning");
      return;
    }

    // If changing password, validate
    if (profileData.newPassword || profileData.confirmPassword) {
      if (!profileData.currentPassword) {
        Swal.fire("Validation", "Current password is required to change password", "warning");
        return;
      }
      if (profileData.newPassword.length < 6) {
        Swal.fire("Validation", "New password must be at least 6 characters", "warning");
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        Swal.fire("Validation", "Passwords do not match", "warning");
        return;
      }
    }

    setIsSaving(true);

    try {
      // Send OTP to user's email
      const response = await fetch(AUTH_BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "send_code",
          email: profileData.email,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setProfileStep(2);
        setCodeTimer(600); // 10 minutes
        Swal.fire({
          title: "OTP Sent",
          text: `A verification code has been sent to ${profileData.email}. Please check your inbox.`,
          icon: "success",
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        Swal.fire("Error", data.message || "Failed to send OTP", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error. Please try again.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  // Step 2: Verify OTP and then update profile
  const handleVerifyAndUpdate = async (e) => {
    e.preventDefault();

    if (otpCode.length !== 6) {
      Swal.fire("Validation", "Please enter the 6-digit OTP", "warning");
      return;
    }
    if (codeTimer === 0) {
      Swal.fire("Expired", "The OTP has expired. Please request a new one.", "warning");
      return;
    }

    setIsVerifying(true);

    try {
      // 1. Verify OTP
      const verifyResponse = await fetch(AUTH_BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "verify_code",
          email: profileData.email,
          code: otpCode,
        }),
      });
      const verifyData = await verifyResponse.json();

      if (!verifyData.success) {
        Swal.fire("Invalid OTP", verifyData.message || "The code is incorrect.", "error");
        setIsVerifying(false);
        return;
      }

      // 2. Proceed with profile update
      const updatePayload = {
        action: "update_profile",
        auth_token: authToken,
        name: profileData.name,
        email: profileData.email,
        mobile: profileData.mobile,
      };

      console.log(updatePayload);

      // Include password fields only if they are provided
      if (profileData.newPassword) {
        updatePayload.current_password = profileData.currentPassword;
        updatePayload.new_password = profileData.newPassword;
      }

      const updateResponse = await fetch(AUTH_BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatePayload),
      });
      const updateData = await updateResponse.json();

      if (updateData.success) {
        // Update localStorage with new user data
        const updatedUser = { ...userData, ...updateData.user };
        localStorage.setItem("userData", JSON.stringify(updatedUser));

        // Send congratulatory email (backend already does this)
        Swal.fire({
          title: "Profile Updated!",
          text: "Your profile has been successfully updated. A confirmation email has been sent.",
          icon: "success",
          timer: 3000,
          timerProgressBar: true,
        });

        setShowProfileModal(false);
        setProfileStep(1);
        setOtpCode("");
      } else {
        Swal.fire("Update Failed", updateData.message || "Could not update profile.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error. Please try again.", "error");
    } finally {
      setIsVerifying(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    if (codeTimer > 300) {
      Swal.fire("Wait", "Please wait before requesting a new OTP.", "warning");
      return;
    }

    try {
      const response = await fetch(AUTH_BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "send_code",
          email: profileData.email,
        }),
      });
      const data = await response.json();

      if (data.success) {
        setCodeTimer(600);
        Swal.fire("Resent", "New OTP sent to your email.", "success");
      } else {
        Swal.fire("Error", data.message || "Failed to resend OTP.", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Network error. Please try again.", "error");
    }
  };

  // ---------- MENU ITEMS (unchanged) ----------
  const menuItems = [
    { path: "/dashboard", label: "Dashboard", icon: faHome, section: "main" },
    { path: "/WhatsAppManager", label: "Message Center", icon: faPaperPlane, section: "main" },
    { path: "/manage-students", label: "Manage Students", icon: faUserGraduate, section: "academic" },
    { path: "/manage-teachers", label: "Manage Teachers", icon: faChalkboardTeacher, section: "academic" },
    { path: "/Manage-Classes-Attendance", label: "Classes Attendance", icon: faSchool, section: "academic" },
    { path: "/Manage-Classes-Tests", label: "Manage Tests", icon: faBook, section: "academic" },
    { path: "/Manage-Classes-Exams", label: "Add Exam Marks", icon: faMarker, section: "academic" },
    { path: "/Accounts", label: "Accounts", icon: faFileInvoiceDollar, section: "finance" },
    { path: "/Result Management Dashboard", label: "Make Results", icon: faChartLine, section: "academic" },
  ];

  const groupedMenuItems = menuItems.reduce((groups, item) => {
    if (!groups[item.section]) groups[item.section] = [];
    groups[item.section].push(item);
    return groups;
  }, {});

  // ---------- RENDER ----------
  return (
    <>
      {/* Mobile Toggle Button */}
      <button
        className="sidebar-toggle"
        onClick={toggleMobileSidebar}
        aria-label="Toggle sidebar"
      >
        <FontAwesomeIcon icon={isMobileOpen ? faChevronLeft : faChevronRight} />
      </button>

      {/* Sidebar */}
      <aside
        className={`sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "active" : ""
        }`}
      >
        {/* Logo Section */}
        <div className="sidebar-logo">
          <div className="sidebar-logo-container">
            <img
              src="/images/sidebarimg.svg"
              alt="MHS Logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/140x110/2E2A74/FFFFFF?text=DEA";
              }}
            />
          </div>
        </div>

        {/* User Profile - clickable to open profile modal */}
        <div className="sidebar-user-profile" onClick={openProfileModal} style={{ cursor: "pointer" }}>
          <div className="sidebar-user-avatar">
            <FontAwesomeIcon icon={faUser} />
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{userData.name || "User"}</div>
            <div className="sidebar-user-role">{userData.role || "User"}</div>
          </div>
          <div className="sidebar-user-status"></div>
        </div>

        {/* Main Menu */}
        <nav className="sidebar-menu">
          {/* Main Section */}
          {groupedMenuItems.main &&
            groupedMenuItems.main.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-menu-item ${
                  activeItem === item.path ? "active" : ""
                }`}
                style={{ "--item-index": index }}
                onClick={() => setIsMobileOpen(false)}
              >
                <div className="sidebar-menu-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="sidebar-menu-counter">{item.badge}</span>
                )}
              </Link>
            ))}

          {/* Academic Section Divider */}
          <div className="sidebar-menu-divider"></div>

          {/* Academic Section */}
          {groupedMenuItems.academic &&
            groupedMenuItems.academic.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-menu-item ${
                  activeItem === item.path ? "active" : ""
                }`}
                style={{ "--item-index": index + 1 }}
                onClick={() => setIsMobileOpen(false)}
              >
                <div className="sidebar-menu-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="sidebar-menu-counter">{item.badge}</span>
                )}
                {item.badge && (
                  <span className="sidebar-menu-badge">{item.badge}</span>
                )}
              </Link>
            ))}

          {/* Finance Section Divider */}
          <div className="sidebar-menu-divider"></div>

          {/* Finance Section */}
          {groupedMenuItems.finance &&
            groupedMenuItems.finance.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-menu-item ${
                  activeItem === item.path ? "active" : ""
                }`}
                style={{ "--item-index": index + 8 }}
                onClick={() => setIsMobileOpen(false)}
              >
                <div className="sidebar-menu-icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <span>{item.label}</span>
                {item.badge && (
                  <span className="sidebar-menu-counter">{item.badge}</span>
                )}
              </Link>
            ))}

          {/* Additional Options */}
          <Link
            to="/Our Users"
            className="sidebar-menu-item"
            style={{ "--item-index": 9 }}
            onClick={() => setIsMobileOpen(false)}
          >
            <div className="sidebar-menu-icon">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <span>Our Users</span>
          </Link>

          <Link
            to="/settings"
            className="sidebar-menu-item"
            style={{ "--item-index": 10 }}
            onClick={() => setIsMobileOpen(false)}
          >
            <div className="sidebar-menu-icon">
              <FontAwesomeIcon icon={faCog} />
            </div>
            <span>Settings</span>
          </Link>

          {/* Logout Button */}
          <button
            className="sidebar-menu-item logout"
            onClick={() => setShowLogoutModal(true)}
            style={{ "--item-index": 11 }}
          >
            <div className="sidebar-menu-icon">
              <FontAwesomeIcon icon={faSignOutAlt} />
            </div>
            <span>Logout</span>
          </button>
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div>DEA School System</div>
          <div className="sidebar-version-badge">v2.1.0</div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* ---------- PROFILE MODAL ---------- */}
      {showProfileModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              animation: "slideUp 0.4s ease-out",
              position: "relative",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0, color: "#2E2A74" }}>
                <FontAwesomeIcon icon={faUser} style={{ marginRight: "10px" }} />
                {profileStep === 1 ? "Edit Profile" : "Verify OTP"}
              </h2>
              <button
                onClick={() => setShowProfileModal(false)}
                disabled={isSaving || isVerifying}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  color: "#666",
                  padding: "5px",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Step 1: Edit Form */}
            {profileStep === 1 && (
              <form onSubmit={handleSaveProfile}>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: "8px" }} />
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "8px" }} />
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faMobileAlt} style={{ marginRight: "8px" }} />
                    Mobile Number
                  </label>
                  <input
                    type="text"
                    name="mobile"
                    value={profileData.mobile}
                    onChange={handleProfileInputChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                    placeholder="Optional"
                  />
                </div>

                <hr style={{ margin: "20px 0", border: "0", borderTop: "1px solid #eee" }} />

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faLock} style={{ marginRight: "8px" }} />
                    Current Password (if changing)
                  </label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={profileData.currentPassword}
                    onChange={handleProfileInputChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                    placeholder="Leave blank if not changing password"
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faKey} style={{ marginRight: "8px" }} />
                    New Password
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    value={profileData.newPassword}
                    onChange={handleProfileInputChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                    placeholder="Min 6 characters"
                    minLength="6"
                  />
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "8px" }} />
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={profileData.confirmPassword}
                    onChange={handleProfileInputChange}
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1rem",
                    }}
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(false)}
                    disabled={isSaving}
                    style={{
                      padding: "10px 20px",
                      background: "#f0f0f0",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    style={{
                      padding: "10px 20px",
                      background: "#2E2A74",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {isSaving ? (
                      <>
                        <FontAwesomeIcon icon={faSyncAlt} spin />
                        Sending OTP...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* Step 2: OTP Verification */}
            {profileStep === 2 && (
              <form onSubmit={handleVerifyAndUpdate}>
                <div style={{ marginBottom: "20px", textAlign: "center" }}>
                  <div style={{ fontSize: "3rem", color: "#2E2A74" }}>
                    <FontAwesomeIcon icon={faKey} />
                  </div>
                  <p style={{ fontSize: "0.95rem", color: "#666" }}>
                    We've sent a 6‑digit verification code to <strong>{profileData.email}</strong>.
                  </p>
                  <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginTop: "10px" }}>
                    <span style={{ fontSize: "0.9rem", color: "#e74c3c" }}>
                      <FontAwesomeIcon icon={faClock} /> Expires in: {Math.floor(codeTimer / 60)}:{String(codeTimer % 60).padStart(2, "0")}
                    </span>
                  </div>
                </div>

                <div style={{ marginBottom: "15px" }}>
                  <label style={{ display: "block", fontWeight: "500", marginBottom: "5px" }}>
                    <FontAwesomeIcon icon={faQrcode} style={{ marginRight: "8px" }} />
                    Verification Code
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={handleOtpInputChange}
                    maxLength="6"
                    placeholder="000000"
                    style={{
                      width: "100%",
                      padding: "10px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      fontSize: "1.2rem",
                      textAlign: "center",
                      letterSpacing: "8px",
                      fontWeight: "bold",
                    }}
                    required
                  />
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={isVerifying || codeTimer > 300}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#2E2A74",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                    }}
                  >
                    Resend Code
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfileStep(1)}
                    disabled={isVerifying}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#666",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                    }}
                  >
                    Back to Edit
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setShowProfileModal(false)}
                    disabled={isVerifying}
                    style={{
                      padding: "10px 20px",
                      background: "#f0f0f0",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isVerifying || codeTimer === 0}
                    style={{
                      padding: "10px 20px",
                      background: "#27ae60",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {isVerifying ? (
                      <>
                        <FontAwesomeIcon icon={faSyncAlt} spin />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheckCircle} />
                        Verify & Update
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ---------- LOGOUT MODAL (unchanged) ---------- */}
      {showLogoutModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "rgba(0, 0, 0, 0.7)",
            zIndex: 10000,
            backdropFilter: "blur(10px)",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              padding: "30px",
              maxWidth: "450px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              transform: isLoggingOut ? "scale(0.95)" : "scale(1)",
              transition: "transform 0.3s ease",
              animation: "slideUp 0.5s ease-out",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, color: "#2E2A74", fontSize: "1.5rem", display: "flex", alignItems: "center", gap: "10px" }}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Confirm Logout
              </h2>
              <button
                onClick={() => !isLoggingOut && setShowLogoutModal(false)}
                disabled={isLoggingOut}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.2rem",
                  cursor: isLoggingOut ? "not-allowed" : "pointer",
                  color: "#666",
                  padding: "5px",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {isLoggingOut ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    border: "3px solid #f3f3f3",
                    borderTop: "3px solid #2E2A74",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    margin: "0 auto 20px",
                  }}
                ></div>
                <p style={{ color: "#2E2A74", fontSize: "1.1rem", fontWeight: "500" }}>Logging out...</p>
                <p style={{ color: "#666", fontSize: "0.9rem", marginTop: "10px" }}>Securing your session and redirecting</p>
              </div>
            ) : (
              <>
                <div style={{ background: "linear-gradient(135deg, #ff6b6b, #ee5a52)", borderRadius: "15px", padding: "20px", marginBottom: "25px", textAlign: "center", color: "white" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "10px", animation: "pulse 2s infinite" }}>⚠️</div>
                  <h3 style={{ margin: "0 0 10px 0", fontSize: "1.2rem" }}>Security Alert</h3>
                  <p style={{ margin: 0, fontSize: "0.9rem", opacity: 0.9 }}>You are about to log out from the system</p>
                </div>

                <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "15px", marginBottom: "25px" }}>
                  <p style={{ margin: 0, color: "#666", fontSize: "0.95rem", lineHeight: 1.5 }}>
                    <strong>Note:</strong> This action will:
                  </p>
                  <ul style={{ margin: "10px 0 0 0", paddingLeft: "20px", color: "#666", fontSize: "0.9rem", lineHeight: 1.6 }}>
                    <li>End your current session</li>
                    <li>Clear all cached data</li>
                    <li>Require re-authentication to access</li>
                    <li>Redirect to login page</li>
                  </ul>
                </div>

                <div style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    style={{ padding: "12px 25px", background: "#f0f0f0", color: "#666", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "0.95rem", fontWeight: "500", transition: "all 0.3s", flex: 1 }}
                    onMouseEnter={(e) => (e.target.style.background = "#e0e0e0")}
                    onMouseLeave={(e) => (e.target.style.background = "#f0f0f0")}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{ padding: "12px 25px", background: "linear-gradient(135deg, #ff6b6b, #ee5a52)", color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "0.95rem", fontWeight: "500", transition: "all 0.3s", flex: 1, boxShadow: "0 4px 15px rgba(238, 90, 82, 0.3)" }}
                    onMouseEnter={(e) => (e.target.style.transform = "translateY(-2px)")}
                    onMouseLeave={(e) => (e.target.style.transform = "translateY(0)")}
                  >
                    Confirm Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ---------- ANIMATION STYLES (unchanged) ---------- */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default Sidebar;